import React from 'react';
import {
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  IconButton,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productEquivalencyCategoriesApi, productEquivalenciesApi, hexahqCacheApi } from '../../api/services';
import type { HexahqSku } from '../../api/services';
import type { ProductEquivalency } from '../../api/config';
import { useHexahqAutocomplete } from '../../hooks/useHexahqAutocomplete';
import { useDebounce } from '../../hooks/useDebounce';
import { FormDialog } from '../FormDialog';

interface ProductEquivalencyFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Pick<ProductEquivalency, 'name' | 'hexa_hq_sku' | 'product_equivalency_category_id' | 'description'>) => void;
  initialData?: ProductEquivalency;
  onExited?: () => void;
  error?: string | null;
  isPending?: boolean;
  /** Set of HexaHQ SKUs already assigned to other product equivalencies. */
  takenSkus?: Set<string>;
  /** Called when user wants to navigate to an existing group (duplicate detected). */
  onNavigateToGroup?: (id: number) => void;
}

export const ProductEquivalencyForm: React.FC<ProductEquivalencyFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  onExited,
  error,
  isPending,
  takenSkus,
  onNavigateToGroup,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = React.useState({
    name: '',
    hexa_hq_sku: '',
    product_equivalency_category_id: 0,
    description: '',
  });
  const [newCategoryName, setNewCategoryName] = React.useState('');
  const [isAddingCategory, setIsAddingCategory] = React.useState(false);

  const sku = useHexahqAutocomplete<HexahqSku>({
    searchFn: hexahqCacheApi.skuSearch,
    queryKey: 'hexahq_sku_search',
    formatLabel: (opt) => opt.name ? `${opt.sku}: ${opt.name}` : opt.sku,
    isOptionEqual: (a, b) => a.sku === b.sku,
    enabled: open,
    noMatchText: 'No matching SKUs',
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['product_equivalency_categories'],
    queryFn: productEquivalencyCategoriesApi.getAll,
    enabled: open,
  });

  const createCategoryMutation = useMutation({
    mutationFn: productEquivalencyCategoriesApi.create,
    onSuccess: (newCategory) => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalency_categories'] });
      setFormData((prev) => ({ ...prev, product_equivalency_category_id: newCategory.id }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    },
  });

  // Reset form when switching between create/edit mode
  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        hexa_hq_sku: initialData.hexa_hq_sku || '',
        product_equivalency_category_id: initialData.product_equivalency_category_id,
        description: initialData.description || '',
      });
      if (initialData.hexa_hq_sku) {
        sku.setSelectedOption({ sku: initialData.hexa_hq_sku, name: initialData.hexahq_product_name || '' });
        sku.setInputValue(initialData.hexa_hq_sku);
      } else {
        sku.setSelectedOption(null);
        sku.setInputValue('');
      }
    } else {
      setFormData({
        name: '',
        hexa_hq_sku: '',
        product_equivalency_category_id: categories[0]?.id || 0,
        description: '',
      });
      sku.setSelectedOption(null);
      sku.setInputValue('');
    }
    setIsAddingCategory(false);
    setNewCategoryName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Default category when categories load and none is selected yet (create mode only).
  // Separated from the reset effect so that invalidating the categories query
  // (e.g. after inline category creation) doesn't wipe user input.
  React.useEffect(() => {
    if (!initialData && categories.length > 0) {
      setFormData((prev) => ({
        ...prev,
        product_equivalency_category_id: prev.product_equivalency_category_id || categories[0].id,
      }));
    }
  }, [initialData, categories]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const skuIsTaken = !!(
    formData.hexa_hq_sku &&
    takenSkus?.has(formData.hexa_hq_sku)
  );

  const debouncedName = useDebounce(formData.name.trim(), 400);
  const debouncedCategoryId = useDebounce(formData.product_equivalency_category_id, 400);

  const { data: nameCheck, isFetching: nameCheckLoading } = useQuery({
    queryKey: ['pe_check_name', debouncedName, debouncedCategoryId, initialData?.id],
    queryFn: () => productEquivalenciesApi.checkName(
      debouncedName,
      debouncedCategoryId,
      initialData?.id,
    ),
    enabled: open && !!debouncedName && !!debouncedCategoryId,
  });

  const nameIsTaken = nameCheck?.exists ?? false;
  const existingGroupId = nameCheck?.id;
  // Disable submit while the debounced check is still in-flight (prevents
  // race where user clicks Update before the check completes).
  const nameIsDirty = formData.name.trim() !== debouncedName || formData.product_equivalency_category_id !== debouncedCategoryId;
  const nameCheckPending = open && !!formData.name.trim() && !!formData.product_equivalency_category_id && (nameIsDirty || nameCheckLoading);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nameIsTaken || nameCheckPending || skuIsTaken) return;
    onSubmit({
      ...formData,
      hexa_hq_sku: formData.hexa_hq_sku || null,
    });
  };

  const handleCreateCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    createCategoryMutation.mutate({ name: trimmed });
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={initialData ? 'Edit Product Equivalency' : 'Create Product Equivalency'}
      submitLabel={initialData ? 'Update' : 'Create'}
      error={error}
      isPending={isPending}
      submitDisabled={!formData.product_equivalency_category_id || skuIsTaken || nameIsTaken || nameCheckPending}
      TransitionProps={{ onExited }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Autocomplete
              freeSolo={false}
              options={sku.options}
              filterOptions={(x) => x}
              getOptionLabel={sku.formatLabel}
              renderOption={(props, option) => (
                <li {...props} key={option.sku}>
                  <Stack>
                    <Typography variant="body2">{option.sku}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                  </Stack>
                </li>
              )}
              value={sku.selectedOption}
              onChange={(_e, newValue) => {
                sku.setSelectedOption(newValue);
                setFormData((prev) => ({ ...prev, hexa_hq_sku: newValue?.sku || '' }));
              }}
              inputValue={sku.inputValue}
              onInputChange={(_e, newInput, reason) => {
                sku.handleInputChange(_e, newInput, reason);
                if (reason === 'clear') {
                  setFormData((prev) => ({ ...prev, hexa_hq_sku: '' }));
                }
              }}
              isOptionEqualToValue={sku.isOptionEqualToValue}
              loading={sku.isLoading}
              noOptionsText={sku.noOptionsText}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="HexaHQ SKU"
                  helperText={
                    skuIsTaken
                      ? 'This SKU is already assigned to another product equivalency.'
                      : 'Search by SKU or product name. Leave blank if not linked to HexaHQ.'
                  }
                  error={skuIsTaken}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {sku.isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              error={nameIsTaken}
            />
            {nameIsTaken && (
              <Alert
                severity="warning"
                sx={{ alignItems: 'center' }}
                action={
                  existingGroupId && onNavigateToGroup ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        onClose();
                        onNavigateToGroup(existingGroupId);
                      }}
                    >
                      View
                    </Button>
                  ) : undefined
                }
              >
                A group with this name already exists in this category. You can add products to the existing group instead.
              </Alert>
            )}
            {isAddingCategory ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label="New Category Name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  fullWidth
                  autoFocus
                  error={createCategoryMutation.isError}
                  helperText={createCategoryMutation.isError ? 'Failed to create category' : undefined}
                />
                <Button
                  variant="contained"
                  onClick={handleCreateCategory}
                  disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                  sx={{ mt: '8px' }}
                >
                  Add
                </Button>
                <Button
                  onClick={() => { setIsAddingCategory(false); setNewCategoryName(''); }}
                  sx={{ mt: '8px' }}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.product_equivalency_category_id || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, product_equivalency_category_id: Number(e.target.value) }))}
                    label="Category"
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Create new category">
                  <IconButton onClick={() => setIsAddingCategory(true)} color="primary">
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
      </Stack>
    </FormDialog>
  );
};
